import { useQuery } from '@tanstack/react-query';
import ms from 'ms';

import { type InvoiceDirection, type InvoicesQueryParams } from './types';
import { getInvoicesQueryOptions } from './useQueryInvoices';

const FAULTY_CHECK_STATUSES = [
  'formal_check_failed',
  'no_check_possible',
  'dln_not_found',
  'dln_not_signed',
  'dln_check_failed',
  'price_check_failed',
] satisfies NonNullable<InvoicesQueryParams['checkStatus']>;

const HOME_INVOICE_REFETCH_INTERVAL = ms('5m');
type UseInvoiceHomeMetricsParams = {
  accountingReferenceIds?: string[];
  direction: InvoiceDirection;
  invoiceDateAfter: string;
  invoiceDateBefore: string;
};

function useInvoiceMetricQuery(queryParams: Partial<InvoicesQueryParams>) {
  return useQuery(
    getInvoicesQueryOptions({
      options: { refetchInterval: HOME_INVOICE_REFETCH_INTERVAL },
      queryParams,
    }),
  );
}

/**
 * Loads bounded invoice totals for a home number tile without fetching every
 * invoice into client state.
 *
 * @param params - Direction, date range, and optional global cost-center ids.
 * @returns Total/faulty counts, latest matching date, and combined query state.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoices_for_user_invoice_get
 */
export function useInvoiceHomeMetrics({
  accountingReferenceIds,
  direction,
  invoiceDateAfter,
  invoiceDateBefore,
}: UseInvoiceHomeMetricsParams) {
  const commonQueryParams = {
    accountingReferenceIds:
      accountingReferenceIds?.length === 0 ? undefined : accountingReferenceIds,
    direction,
    invoiceDateAfter,
    invoiceDateBefore,
    limit: 1,
    offset: 0,
    orderBy: 'date',
    sortOrder: 'DESC',
  } satisfies Partial<InvoicesQueryParams>;

  const totalQuery = useInvoiceMetricQuery(commonQueryParams);
  const faultyQuery = useInvoiceMetricQuery({
    ...commonQueryParams,
    checkStatus: FAULTY_CHECK_STATUSES,
  });

  return {
    faultyCount: faultyQuery.data?.totalCount ?? 0,
    isError:
      totalQuery.isError ||
      faultyQuery.isError ||
      totalQuery.data?.totalCount === null ||
      faultyQuery.data?.totalCount === null,
    isLoading: totalQuery.isLoading || faultyQuery.isLoading,
    latestInvoiceDate: totalQuery.data?.data?.[0]?.date,
    totalCount: totalQuery.data?.totalCount ?? 0,
  };
}
